import { computed, reactive, readonly, ref } from 'vue'
import { getConfig } from '@/config'
import { apiRoutes } from '@/services/botApi'
import { getApiClient, type ApiRequestConfig } from '@/services/http'
import { authenticationRecovery } from '@/auth/recovery'
import { authSurfaceOf, casRedirectHandlesAuthFailure } from '@/auth/authSurfacePolicy'
import type { Router } from 'vue-router'

export interface AuthUser {
  sub: string
  org_id: string
  email?: string | null
  name?: string | null
}

export type AuthSource = 'cas' | 'lti_study' | 'lti_assignment'

export interface AuthSessionPayload {
  accessToken: string
  expiresAt: number
  user: AuthUser
  authSource?: AuthSource | null
  launch?: { botId: string; conversationSessionId?: string }
}

type CasExchangeResponse = Omit<AuthSessionPayload, 'authSource'>

interface AuthSessionExchangeResponse {
  authSession: CasExchangeResponse
}

interface AssignmentLtiLaunchResponse extends AuthSessionExchangeResponse {
  conversationSessionId: string
}

const AUTH_STORAGE_KEY = 'chat.auth.session'
const RETURN_TO_STORAGE_KEY = 'chat.auth.return_to'
const REDIRECT_PAUSED_STORAGE_KEY = 'chat.auth.redirect_paused'
const REDIRECT_CHAT_STORAGE_KEY = 'chat.auth.redirect_chat'
const LAST_TICKET_STORAGE_KEY = 'chat.auth.last_ticket'
const ANONYMOUS_SESSION_CLAIM_STORAGE_KEY = 'chat.auth.anonymous_session_claim'
export const AUTH_FAILURE_REDIRECT_EVENT = 'chat:auth-failure-redirect'
export interface AuthFailureRedirectEventDetail {
  status: 401
  reason: 'auth_required' | 'expired'
}

export interface AnonymousSessionClaimTarget {
  botId: string
  botName: string
  sessionId: string
}

export interface RedirectChatState {
  user: AuthUser | null
  botName: string
  sessionId: string | null
  draft: string
}

export function saveRedirectChatState(context: RedirectChatState) {
  writeSessionStorage(REDIRECT_CHAT_STORAGE_KEY, JSON.stringify(context))
}

export function consumeRedirectChatState(): RedirectChatState | null {
  const raw = readSessionStorage(REDIRECT_CHAT_STORAGE_KEY)
  removeSessionStorage(REDIRECT_CHAT_STORAGE_KEY)
  if (!raw) return null
  try {
    const context = JSON.parse(raw) as RedirectChatState
    return typeof context.botName === 'string' && typeof context.draft === 'string'
      && (context.sessionId === null || typeof context.sessionId === 'string')
      && (context.user === null || (typeof context.user?.sub === 'string' && typeof context.user.org_id === 'string')) ? context : null
  } catch { return null }
}

export const autoLoginRedirectSuppressed = ref(false)
let sessionExpiryTimer: ReturnType<typeof setTimeout> | null = null
let sessionExpiryLifecycleListenerRegistered = false

const MAX_TIMEOUT_DELAY_MS = 2_147_483_647
const CLOCK_SKEW_TOLERANCE_MS = 60_000

const authState = reactive<{
  initialized: boolean
  loading: boolean
  isAuthenticated: boolean
  accessToken: string | null
  expiresAt: number | null
  user: AuthUser | null
  authSource: AuthSource | null
  launch: AuthSessionPayload['launch']
}>({
  initialized: false,
  loading: false,
  isAuthenticated: false,
  accessToken: null,
  expiresAt: null,
  user: null,
  authSource: null,
  launch: undefined,
})

function readSessionStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch (error) {
    logSessionStorageFailure('read', key, error)
    return null
  }
}

function writeSessionStorage(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value)
  } catch (error) {
    logSessionStorageFailure('write', key, error)
  }
}

function removeSessionStorage(key: string) {
  try {
    window.sessionStorage.removeItem(key)
  } catch (error) {
    logSessionStorageFailure('remove', key, error)
  }
}

function logSessionStorageFailure(
  operation: 'read' | 'write' | 'remove',
  key: string,
  error: unknown,
) {
  if (!import.meta.env.DEV) return
  console.warn(`[auth] Failed to ${operation} sessionStorage key "${key}"`, error)
}

function isStoredSessionValid(session: AuthSessionPayload): boolean {
  return Boolean(
    isStoredSessionWellFormed(session)
    && session.expiresAt * 1000 - CLOCK_SKEW_TOLERANCE_MS > Date.now(),
  )
}

function isStoredSessionWellFormed(session: AuthSessionPayload): boolean {
  return Boolean(
    session && typeof session.accessToken === 'string' && session.accessToken
    && typeof session.user?.sub === 'string' && session.user.sub
    && typeof session.user.org_id === 'string' && session.user.org_id
    && Number.isFinite(session.expiresAt),
  )
}

/** Validate external exchange/message data before it reaches the lifecycle. */
export function parseAuthSessionPayload(payload: unknown): AuthSessionPayload {
  const session = payload as AuthSessionPayload
  if (!isStoredSessionValid(session)) throw new Error('Received an invalid login response. Try again.')
  return session
}

function clearSessionExpiryTimer() {
  if (sessionExpiryTimer == null) return
  clearTimeout(sessionExpiryTimer)
  sessionExpiryTimer = null
}

function dispatchExpiredSessionEvent() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<AuthFailureRedirectEventDetail>(
    AUTH_FAILURE_REDIRECT_EVENT,
    { detail: { status: 401, reason: 'expired' } },
  ))
}

/**
 * Starts the one recovery transaction used for both a rejected bearer token
 * and a token whose locally-known expiry time has elapsed.
 */
function beginExpiredSessionRecovery() {
  if (!authenticationRecovery.requireAuthentication('expired')) return
  clearAuthSession()
  dispatchExpiredSessionEvent()
}

function expireSessionIfDue() {
  if (!authState.accessToken || !authState.expiresAt) return
  if (authState.expiresAt * 1000 - CLOCK_SKEW_TOLERANCE_MS > Date.now()) return
  beginExpiredSessionRecovery()
}

function scheduleSessionExpiry() {
  clearSessionExpiryTimer()

  const scheduleNextCheck = () => {
    if (!authState.expiresAt) return
    const remainingMs = authState.expiresAt * 1000 - CLOCK_SKEW_TOLERANCE_MS - Date.now()
    if (remainingMs <= 0) {
      expireSessionIfDue()
      return
    }
    sessionExpiryTimer = setTimeout(
      scheduleNextCheck,
      Math.min(remainingMs, MAX_TIMEOUT_DELAY_MS),
    )
  }

  scheduleNextCheck()
}

function ensureSessionExpiryLifecycleListener() {
  if (sessionExpiryLifecycleListenerRegistered || typeof window === 'undefined') return

  window.addEventListener('focus', expireSessionIfDue)
  document.addEventListener('visibilitychange', expireSessionOnVisibilityChange)
  sessionExpiryLifecycleListenerRegistered = true
}

function expireSessionOnVisibilityChange() {
  if (document.visibilityState === 'visible') expireSessionIfDue()
}

function removeSessionExpiryLifecycleListener() {
  if (!sessionExpiryLifecycleListenerRegistered || typeof window === 'undefined') return
  window.removeEventListener('focus', expireSessionIfDue)
  document.removeEventListener('visibilitychange', expireSessionOnVisibilityChange)
  sessionExpiryLifecycleListenerRegistered = false
}

function readStoredSession(): AuthSessionPayload | null {
  const raw = readSessionStorage(AUTH_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as AuthSessionPayload
    return isStoredSessionWellFormed(parsed) ? parsed : null
  } catch {
    return null
  }
}

let requestVersion = 0
let requestController = new AbortController()
export function getAuthRequestContext() {
  return { version: requestVersion, signal: requestController.signal }
}

function applySession(session: AuthSessionPayload | null) {
  requestController.abort()
  requestController = new AbortController()
  requestVersion += 1
  clearSessionExpiryTimer()
  removeSessionExpiryLifecycleListener()
  authState.isAuthenticated = Boolean(session)
  authState.accessToken = session?.accessToken ?? null
  authState.expiresAt = session?.expiresAt ?? null
  authState.user = session?.user ?? null
  authState.authSource = session?.authSource ?? null
  authState.launch = session?.launch
  if (session) {
    writeSessionStorage(AUTH_STORAGE_KEY, JSON.stringify(session))
    ensureSessionExpiryLifecycleListener()
    scheduleSessionExpiry()
  } else {
    removeSessionStorage(AUTH_STORAGE_KEY)
  }
}

function getCallbackServiceUrl(): string {
  return new URL('/auth/callback', window.location.origin).toString()
}

export function clearLoginFailure() {
  autoLoginRedirectSuppressed.value = false
  removeSessionStorage(REDIRECT_PAUSED_STORAGE_KEY)
}

export function recordLoginFailure() {
  autoLoginRedirectSuppressed.value = true
  writeSessionStorage(REDIRECT_PAUSED_STORAGE_KEY, 'true')
}

export function saveReturnTo(returnTo: string) {
  writeSessionStorage(RETURN_TO_STORAGE_KEY, returnTo)
}

export function peekReturnTo(): string | null {
  return readSessionStorage(RETURN_TO_STORAGE_KEY)
}

export function consumeReturnTo(): string | null {
  const value = readSessionStorage(RETURN_TO_STORAGE_KEY)
  removeSessionStorage(RETURN_TO_STORAGE_KEY)
  return value
}

export function normalizeAuthReturnToPath(path: string | null): string {
  if (
    !path
    || !path.startsWith('/')
    || path.startsWith('//')
    || path.startsWith('/\\')
    || path.startsWith('/auth/callback')
    || path.startsWith('/login')
  ) return '/'
  return path
}

export function consumeAuthReturnTo(): string {
  return normalizeAuthReturnToPath(consumeReturnTo())
}

export function saveAnonymousSessionClaimTarget(target: AnonymousSessionClaimTarget | null) {
  if (!target || authState.isAuthenticated || authState.authSource !== null) return
  writeSessionStorage(ANONYMOUS_SESSION_CLAIM_STORAGE_KEY, JSON.stringify(target))
}

/** Registers the active anonymous conversation without coupling auth to Pinia. */
export function getAnonymousSessionClaimTarget(): AnonymousSessionClaimTarget | null {
  const rawTarget = readSessionStorage(ANONYMOUS_SESSION_CLAIM_STORAGE_KEY)
  if (!rawTarget) return null
  try {
    const target = JSON.parse(rawTarget) as Partial<AnonymousSessionClaimTarget>
    if (!target.botId || !target.botName || !target.sessionId) return null
    return {
      botId: target.botId,
      botName: target.botName,
      sessionId: target.sessionId,
    }
  } catch {
    return null
  }
}

export function clearAnonymousSessionClaimTarget() {
  removeSessionStorage(ANONYMOUS_SESSION_CLAIM_STORAGE_KEY)
}

export function hasProcessedTicket(ticket: string): boolean {
  return readSessionStorage(LAST_TICKET_STORAGE_KEY) === ticket
}

export function markTicketProcessed(ticket: string) {
  writeSessionStorage(LAST_TICKET_STORAGE_KEY, ticket)
}

export function clearProcessedTicket() {
  removeSessionStorage(LAST_TICKET_STORAGE_KEY)
}

export async function exchangeStudyLtiJwt(ltiJwt: string, botId: string, signal: AbortSignal): Promise<AuthSessionPayload> {
  const { data } = await getApiClient().post<AuthSessionExchangeResponse>(
    apiRoutes.auth.ltiStudyLaunch.path(), { lti_jwt: ltiJwt, bot_id: botId },
    { skipAuthRedirect: true, signal } as ApiRequestConfig,
  )
  return parseAuthSessionPayload({ ...data.authSession, authSource: 'lti_study', launch: { botId } })
}

export async function exchangeAssignmentLtiJwt(assignmentJwt: string, botId: string, signal: AbortSignal): Promise<AuthSessionPayload> {
  const { data } = await getApiClient().post<AssignmentLtiLaunchResponse>(
    apiRoutes.auth.ltiLaunch.path(), { assignment_jwt: assignmentJwt, bot_id: botId },
    { skipAuthRedirect: true, signal } as ApiRequestConfig,
  )
  return parseAuthSessionPayload({ ...data.authSession, authSource: 'lti_assignment', launch: { botId, conversationSessionId: data.conversationSessionId } })
}

export async function exchangeCasTicket(ticket: string, service: string, signal: AbortSignal): Promise<AuthSessionPayload> {
  const { data } = await getApiClient().post<CasExchangeResponse>(
    apiRoutes.auth.exchangeCasTicket.path(), { ticket, service },
    { skipAuthRedirect: true, signal } as ApiRequestConfig,
  )
  return parseAuthSessionPayload({ ...data, authSource: 'cas' })
}

export async function initializeAuth(): Promise<void> {
  if (authState.initialized || authState.loading) return
  authState.loading = true
  try {
    const stored = readStoredSession()
    autoLoginRedirectSuppressed.value = readSessionStorage(REDIRECT_PAUSED_STORAGE_KEY) === 'true'
    // A cold load has no in-memory conversation to recover. Let the requested
    // resource determine whether login is needed, just as for a new visitor.
    applySession(stored && isStoredSessionValid(stored) ? stored : null)
    authState.initialized = true
  } catch {
    applySession(null)
    authState.initialized = true
  } finally {
    authState.loading = false
  }
}

export function clearAuthSession() {
  applySession(null)
}

export async function logout(): Promise<void> {
  const token = authState.accessToken
  authenticationRecovery.resetAuthentication()
  authenticationRecovery.clearUserData()
  clearAuthSession()
  clearLoginFailure()
  removeSessionStorage(RETURN_TO_STORAGE_KEY)
  clearProcessedTicket()
  clearAnonymousSessionClaimTarget()
  removeSessionStorage(REDIRECT_CHAT_STORAGE_KEY)
  if (token) {
    void getApiClient().post(
      apiRoutes.auth.logout.path(),
      undefined,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        skipAuthRedirect: true,
      } as ApiRequestConfig,
    ).catch(() => undefined)
  }
}

export function handleAuthorizationFailure(status: number) {
  if (status !== 401) return
  beginExpiredSessionRecovery()
}

export function notifyAuthorizationRequired() {
  const state = authenticationRecovery.state.value
  const reason = state.phase !== 'idle' && state.reason === 'expired' ? 'expired' : 'auth_required'
  if (!authenticationRecovery.requireAuthentication(reason)) return
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<AuthFailureRedirectEventDetail>(
      AUTH_FAILURE_REDIRECT_EVENT, { detail: { status: 401, reason } },
    ))
  }
}

export function registerAuthorizationFailureRedirect(router: Router) {
  if (typeof window === 'undefined') return

  let redirectInFlight = false

  window.addEventListener(AUTH_FAILURE_REDIRECT_EVENT, async (event: Event) => {
    if (redirectInFlight) return

    const authFailure = event as CustomEvent<AuthFailureRedirectEventDetail>
    const currentRoute = router.currentRoute.value
    if (!casRedirectHandlesAuthFailure(authFailure.detail?.reason, authSurfaceOf(currentRoute.meta))) {
      return
    }
    if (currentRoute.name === 'login' || currentRoute.name === 'auth-callback') {
      return
    }

    redirectInFlight = true
    try {
      saveReturnTo(currentRoute.fullPath)
      await router.replace({
        name: 'login',
        query: { reason: authFailure.detail.reason },
      })
    } finally {
      redirectInFlight = false
    }
  })
}

export function getAccessToken(): string | null {
  return authState.accessToken
}

export function getAuthSessionPayload(): AuthSessionPayload | null {
  if (!authState.isAuthenticated || !authState.accessToken || !authState.expiresAt || !authState.user) {
    return null
  }

  return {
    accessToken: authState.accessToken,
    expiresAt: authState.expiresAt,
    user: {
      sub: authState.user.sub,
      org_id: authState.user.org_id,
      email: authState.user.email ?? null,
      name: authState.user.name ?? null,
    },
    authSource: authState.authSource ?? null,
    launch: authState.launch,
  }
}

export function applyAuthSessionPayload(
  payload: AuthSessionPayload,
): boolean {
  if (!isStoredSessionValid(payload)) {
    return false
  }

  applySession(payload)
  authState.initialized = true
  return true
}

export function getAuthDisplayName(): string {
  return authState.user?.name ?? authState.user?.email ?? authState.user?.sub ?? 'Guest'
}

export function getAuthLoginName(): string {
  return authState.user?.sub ?? authState.user?.email ?? 'Guest'
}

export function buildCasLoginUrl(mode: 'redirect' | 'popup' = 'redirect'): string {
  const { casBaseUrl, casLoginUrl } = getConfig()
  const configuredLoginUrl = casLoginUrl ?? (casBaseUrl ? `${casBaseUrl.replace(/\/$/, '')}/login` : null)
  if (!configuredLoginUrl) {
    throw new Error('CAS login is not configured')
  }
  const loginUrl = new URL(configuredLoginUrl)
  const service = new URL(getCallbackServiceUrl())
  if (mode === 'popup') service.searchParams.set('mode', 'popup')
  loginUrl.searchParams.set('service', service.toString())
  return loginUrl.toString()
}

export function buildCasLogoutUrl(returnTo = new URL('/login?signed_out=true', window.location.origin).toString()): string {
  const { casBaseUrl, casLogoutUrl } = getConfig()
  const configuredLogoutUrl = casLogoutUrl ?? (casBaseUrl ? `${casBaseUrl.replace(/\/$/, '')}/logout` : null)
  if (!configuredLogoutUrl) {
    throw new Error('CAS logout is not configured')
  }
  const logoutUrl = new URL(configuredLogoutUrl)
  logoutUrl.searchParams.set('service', returnTo)
  return logoutUrl.toString()
}

export function startCasLoginRedirect(returnTo?: string, mode: 'redirect' | 'popup' = 'redirect') {
  saveLoginReturnTo(returnTo)
  authenticationRecovery.prepareRedirect()
  recordLoginFailure()
  window.location.assign(buildCasLoginUrl(mode))
}

export function saveLoginReturnTo(returnTo?: string) {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  saveReturnTo(returnTo ?? currentPath)
}

export const authSession = readonly(authState)
export const isAuthenticated = computed(() => authState.isAuthenticated)
